/**
 * @description 功能：安全用户信息新拆分接口
 * @returns react hook
 */
import { clearCache, useRequest, useUpdateEffect } from 'ahooks';
import { useEffect, useMemo } from 'react';

import { clearFetchCache } from '@/services/request';
import { getUserSecurityProfile } from '@/services/security';
import { IBaseResponse } from '@/services/types/common';
import { IUserSecurityInfo } from '@/services/types/common';
import { useSecuritySettingModalData } from '@/store/useSecuritySettingModal/useSecuritySettingModalData';

const CACHE_KEY = 'userSecurityInfo';
let uid = 0;

type mutateType =
  | IBaseResponse<IUserSecurityInfo>
  | ((
      oldData?: IBaseResponse<IUserSecurityInfo>,
    ) => IBaseResponse<IUserSecurityInfo> | undefined);

export function useFetchUserSecurityProfile() {
  const {
    data: profile,
    error,
    loading,
    refresh,
    run,
    mutate: mutateUserInfo,
  } = useRequest<IBaseResponse<IUserSecurityInfo>, []>(getUserSecurityProfile, {
    cacheKey: CACHE_KEY,
    staleTime: -1, // data is considered fresh so we call this api only once
    cacheTime: -1, // never clear cache so we call this api only once
    manual: true,
  });

  const userSecurityInfo = useMemo(() => {
    let empty: any = {};
    if (profile?.result) {
      localStorage.setItem('u-v2', JSON.stringify(profile?.result));
    } else if (typeof localStorage.getItem('u-v2') === 'string') {
      try {
        const str = localStorage.getItem('u-v2') as string;
        empty = JSON.parse(str);
      } catch (e) {
        /* empty */
      }
    }
    return profile?.result || empty;
  }, [profile?.result]);

  const id = useMemo(() => {
    return (userSecurityInfo as IUserSecurityInfo)?.id || 0;
  }, [userSecurityInfo]);

  const mutate = (callback: mutateType) => {
    mutateUserInfo(callback);
    uid = 0;
  };
  const onSuccess = () => {
    uid = id;
    window.GA_UID = id;
  };

  useUpdateEffect(() => {
    if (id > 0 && uid !== id && !loading) {
      onSuccess();
    } else if (!loading && id === 0) {
      //redirectUrlMap.redirectLogin();
    }
  }, [profile?.result]);

  const updateUserSecurityProfile = (
    cb?: (profile: IBaseResponse<IUserSecurityInfo>) => void,
  ) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(
        '%cupdateUserProfile called. you can get result data from your callback function input parameters',
        'color: green',
      );
    }

    clearCache(CACHE_KEY);
    clearFetchCache('profile_v2');
    refresh();
    cb && cb(profile as IBaseResponse<IUserSecurityInfo>);
  };

  return {
    error,
    loading,
    profile,
    userSecurityInfo,
    mutate,
    updateUserSecurityProfile,
    run,
  };
}

export function useUserSecurityProfile(onError?: (error: any) => void) {
  const { userSecurityProfile, getProfileV2 } = useSecuritySettingModalData();

  useEffect(() => {
    getProfileV2?.();
  }, []);

  useEffect(() => {
    if (typeof userSecurityProfile.error !== 'undefined') {
      onError?.(userSecurityProfile.error);
    }
  }, [userSecurityProfile.error]);

  return userSecurityProfile;
}
